import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Upload, FileText, File } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useMutation } from "@tanstack/react-query";
import { getUploadUrlCsv, getUploadUrlSpec } from "@/requests/internal-products/get-upload-url";

export function UploadInterface() {
  const { toast } = useToast();
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [uploadingSpec, setUploadingSpec] = useState(false);

  // Get pre-signed URL mutations
  const getUrlCsv = useMutation({
    mutationFn: getUploadUrlCsv,
  });

  const getUrlSpec = useMutation({
    mutationFn: getUploadUrlSpec,
  });

  // Handle file upload to S3
  const uploadToS3 = async (file: File, uploadUrl: string) => {
    console.log("[UploadInterface] Uploading to S3:", { filename: file.name, size: file.size, url: uploadUrl.substring(0, 100) + "..." });

    const response = await fetch(uploadUrl, {
      method: "PUT",
      body: file,
      headers: {
        "Content-Type": file.type,
      },
    });

    console.log("[UploadInterface] S3 upload response:", { status: response.status, ok: response.ok });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("[UploadInterface] S3 upload failed:", errorText);
      throw new Error(`Upload failed: ${response.statusText}`);
    }

    console.log("[UploadInterface] S3 upload successful");
  };

  // Handle CSV file selection
  const handleCsvUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingCsv(true);

      // Get pre-signed URL
      console.log("[UploadInterface] Requesting CSV upload URL for:", file.name);
      const result = await getUrlCsv.mutateAsync(file.name);
      console.log("[UploadInterface] Received upload URL response:", { bucket: result.bucket, key: result.key });

      // Upload to S3
      await uploadToS3(file, result.uploadUrl);

      toast({
        title: "CSV uploaded successfully",
        description: `File: ${file.name} uploaded to S3. Parse Lambda will process it shortly.`,
      });

      // Clear input
      event.target.value = "";
    } catch (error) {
      console.error("CSV upload error:", error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setUploadingCsv(false);
    }
  };

  // Handle spec sheet file selection
  const handleSpecUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingSpec(true);

      // Get pre-signed URL
      console.log("[UploadInterface] Requesting spec upload URL for:", file.name);
      const result = await getUrlSpec.mutateAsync(file.name);
      console.log("[UploadInterface] Received upload URL response:", { bucket: result.bucket, key: result.key });

      // Upload to S3
      await uploadToS3(file, result.uploadUrl);

      toast({
        title: "Spec sheet uploaded successfully",
        description: `File: ${file.name} uploaded to S3. Extract Lambda will process it shortly.`,
      });

      // Clear input
      event.target.value = "";
    } catch (error) {
      console.error("Spec upload error:", error);
      toast({
        title: "Upload failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive",
      });
    } finally {
      setUploadingSpec(false);
    }
  };

  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* CSV Upload */}
      <div className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center space-y-4 hover:border-primary/50 transition-colors">
        <FileText className="h-12 w-12 text-muted-foreground" />
        <div className="text-center">
          <h3 className="font-semibold">Upload CSV File</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Regional product catalog CSV
          </p>
        </div>
        <label htmlFor="csv-upload">
          <Button disabled={uploadingCsv} asChild>
            <span className="cursor-pointer">
              <Upload className="mr-2 h-4 w-4" />
              {uploadingCsv ? "Uploading..." : "Select CSV File"}
            </span>
          </Button>
        </label>
        <input
          id="csv-upload"
          type="file"
          accept=".csv"
          className="hidden"
          onChange={handleCsvUpload}
          disabled={uploadingCsv}
        />
      </div>

      {/* Spec Sheet Upload */}
      <div className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center space-y-4 hover:border-primary/50 transition-colors">
        <File className="h-12 w-12 text-muted-foreground" />
        <div className="text-center">
          <h3 className="font-semibold">Upload Spec Sheet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Product specification PDF
          </p>
        </div>
        <label htmlFor="spec-upload">
          <Button disabled={uploadingSpec} asChild>
            <span className="cursor-pointer">
              <Upload className="mr-2 h-4 w-4" />
              {uploadingSpec ? "Uploading..." : "Select PDF File"}
            </span>
          </Button>
        </label>
        <input
          id="spec-upload"
          type="file"
          accept=".pdf"
          className="hidden"
          onChange={handleSpecUpload}
          disabled={uploadingSpec}
        />
      </div>
    </div>
  );
}
